import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { ProjectService } from '../lib/projectService';
import { Project, Character2DLayered, CharacterPassport, CharacterArchetype, CharacterForm, CharacterVoice, SavedVoiceSample, ReferenceImage } from '../types';
// Legacy CharacterBuilder2D removed - all characters use new flow
import { CharacterProfilePage } from './CharacterProfilePage';
import PromptBuilder from './characterBuilder2D/PromptBuilder';
import DualPromptReview from './characterBuilder2D/DualPromptReview';
import DualViewportEditor, { DualViewportResult, ViewportState } from './characterBuilder2D/DualViewportEditor';
import CharacterFeatureEditor from './characterBuilder2D/CharacterFeatureEditor';
import type { FeatureEditorSession } from '../lib/characterFeatureTypes';
import ReferenceSheetPreview from './characterBuilder2D/ReferenceSheetPreview';
import { CharacterConceptAnalysis, SplitPrompts } from '../lib/geminiCharacterAnalysis';
import { ProductionBar } from './ProductionBar';
import { saveCharacter2D, saveCharacterPassport, getCharactersByProjectId, getCharacterById, deleteCharacter2D, finalizeCharacterImages, cleanupTempCharacterImages, uploadTempCharacterImage } from '../lib/character2dService';
import { generateCharacterPassport } from '../lib/characterPassportService';
import { getVoicesByCharacterIds, deleteVoiceByCharacterId } from '../lib/characterVoiceService';
import { useToast } from '../context/ToastContext';
import { FolderIcon, PlusIcon, CaretLeftIcon, CaretRightIcon, UsersIcon, UserIcon, SpeakerSimpleHigh, SpeakerSimpleSlash, SpeakerSimpleX, Trash, TrashSimple, WarningCircle, MagnifyingGlassIcon, Pencil } from '@phosphor-icons/react';
import { loadSession } from '../lib/characterSessionStorage';
import { getStylePresetForArchetype } from '../lib/characterArchetypes';
import { generateId } from '../lib/utils';
import { CharacterNameModal } from './CharacterNameModal';
import { StyleOption } from '../lib/characterStyles';

interface Character2DBuilderPageProps {
  projectId?: string; // Pre-selected project ID from URL
  onBack: () => void;
}

export const Character2DBuilderPage: React.FC<Character2DBuilderPageProps> = ({
  projectId: initialProjectId,
  onBack
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { selectedProjectId, setSelectedProjectId } = useProject();
  const permissions = useProjectPermissions(selectedProjectId || undefined);
  const location = useLocation();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedArchetype, setSelectedArchetype] = useState<CharacterArchetype | null>(null);
  const [selectedForm, setSelectedForm] = useState<CharacterForm>('humanoid');
  const [selectedModel, setSelectedModel] = useState<'official' | 'testing' | null>(null);
  const [existingCharacter, setExistingCharacter] = useState<Character2DLayered | null>(null);
  const [initialShowCompositor, setInitialShowCompositor] = useState(false); // Restore compositor view on resume
  const [completedCharacters, setCompletedCharacters] = useState<Character2DLayered[]>([]);

  // New builder phase system (legacy-builder removed - all characters use new flow)
  type BuilderPhase = 'prompt-builder' | 'dual-review' | 'dual-viewport' | 'feature-editor' | 'reference-sheet-preview' | null;
  const [builderPhase, setBuilderPhase] = useState<BuilderPhase>(null);
  const [promptBuilderData, setPromptBuilderData] = useState<{
    description: string;
    selectedTags: string[];
    analysis: CharacterConceptAnalysis | null;
    selectedStyle?: StyleOption;
    referenceImages?: ReferenceImage[];
  } | null>(null);
  const [dualReviewData, setDualReviewData] = useState<{
    bodyPrompt: string;
    headPrompt: string;
    sharedAttributes: SplitPrompts['sharedAttributes'];
    selectedStyle?: StyleOption;
    referenceImages?: ReferenceImage[];
    sourceDescription?: string;  // Track description used to generate prompts
    classifiedImages?: SplitPrompts['classifiedImages'];  // AI-classified reference images
  } | null>(null);
  const [dualViewportResult, setDualViewportResult] = useState<DualViewportResult | null>(null);
  // Reference sheet preview state
  const [referenceSheetUrl, setReferenceSheetUrl] = useState<string | null>(null);
  const [referenceSheetUnclothedUrl, setReferenceSheetUnclothedUrl] = useState<string | null>(null);
  const [referenceSheetVersion, setReferenceSheetVersion] = useState<'original' | 'unclothed'>('original');
  const [isSavingReferenceSheet, setIsSavingReferenceSheet] = useState(false);
  const [pendingCharacterData, setPendingCharacterData] = useState<Character2DLayered | null>(null);
  const [pendingPassportData, setPendingPassportData] = useState<{
    textData: { full_description: string };
    usage_prompt: string;
  } | null>(null);
  // Key to force DualViewportEditor remount when re-entering (prevents stale state)
  const [viewportKey, setViewportKey] = useState(0);
  // Pre-generate character ID for new characters, or restore from localStorage
  const [pendingCharacterId, setPendingCharacterId] = useState(() => {
    const saved = localStorage.getItem('ezart_pending_character_id');
    if (saved) return saved;
    const newId = generateId();
    localStorage.setItem('ezart_pending_character_id', newId);
    return newId;
  });
  const [isLoading, setIsLoading] = useState(true);
  // Track character data loading for resume - initialize based on URL having characterId
  const [isLoadingCharacterData, setIsLoadingCharacterData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCharacterId = !!params.get('characterId');
    const hasWorkflowState = !!params.get('archetype') && !!params.get('model');
    // Only set loading if URL has complete workflow state (means we're resuming)
    return hasCharacterId && hasWorkflowState;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingRefSheet, setIsGeneratingRefSheet] = useState(false); // Reference sheet generation loading
  const [showProjectSelector, setShowProjectSelector] = useState(!initialProjectId && !selectedProjectId);
  const [showResumeOrNew, setShowResumeOrNew] = useState(false); // New state for resume/new choice
  const [showCharacterProfile, setShowCharacterProfile] = useState<string | null>(null); // Character ID to show profile for
  const [showNameModal, setShowNameModal] = useState(false); // New state for name modal
  const [characterName, setCharacterName] = useState(''); // Store character name
  const [searchQuery, setSearchQuery] = useState(''); // Search/create input value
  const [isCreatingNew, setIsCreatingNew] = useState(false); // Flag to prevent auto-loading existing character
  const [currentPage, setCurrentPage] = useState(1);
  const [refetchTrigger, setRefetchTrigger] = useState(0); // Trigger to force refetch character list
  const projectsPerPage = 10;

  // Voice playback state
  const [characterVoices, setCharacterVoices] = useState<Record<string, { hasVoice: boolean; audioUrl: string | null }>>({});
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Drag-and-drop deletion state
  const [draggingCharacterId, setDraggingCharacterId] = useState<string | null>(null);
  const [isOverTrash, setIsOverTrash] = useState(false);
  const [deleteConfirmCharacter, setDeleteConfirmCharacter] = useState<Character2DLayered | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [dragStartRect, setDragStartRect] = useState<{ left: number; top: number } | null>(null);

  // Character name editing state
  const [editingCharacterId, setEditingCharacterId] = useState<string | null>(null);
  const [editingCharacterName, setEditingCharacterName] = useState('');

  // Sidebar state for adaptive grid columns and pill positioning
  const [sidebarState, setSidebarState] = useState(() => {
    if (typeof window !== 'undefined') {
      const isSidebarOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
      const isRightPanelOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
      const navPosition = localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left';
      return { isSidebarOpen, isRightPanelOpen, navPosition, leftOpen: isSidebarOpen, rightOpen: isRightPanelOpen };
    }
    return { isSidebarOpen: true, isRightPanelOpen: false, navPosition: 'left', leftOpen: true, rightOpen: false };
  });

  // Listen for sidebar state changes
  useEffect(() => {
    const updateSidebarState = () => {
      const isSidebarOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
      const isRightPanelOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
      const navPosition = localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left';
      setSidebarState({ isSidebarOpen, isRightPanelOpen, navPosition, leftOpen: isSidebarOpen, rightOpen: isRightPanelOpen });
    };

    window.addEventListener('storage', updateSidebarState);
    const interval = setInterval(updateSidebarState, 200);

    return () => {
      window.removeEventListener('storage', updateSidebarState);
      clearInterval(interval);
    };
  }, []);

  // Calculate grid columns based on sidebar state
  // 4 columns when all sidebars closed, 3 when creative sidebar open
  const gridColumns = sidebarState.rightOpen ? 3 : 4;

  // Filter characters by search query
  const filteredCharacters = searchQuery.trim()
    ? completedCharacters.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : completedCharacters;

  // Track if init has already run to prevent re-navigation on auth state changes
  const hasInitialized = useRef(false);

  // Ref to track if we've attempted to resume a character for the current project
  const hasAttemptedResume = useRef<string | null>(null);
  // Ref to track if user deliberately backed out - prevents auto-resume from re-triggering
  const hasBackedOut = useRef<boolean>(false);

  // Constants for LocalStorage keys
  const KEYS = {
    PROJECT: 'ezart_builder_project_id',
    ARCHETYPE: 'ezart_builder_archetype',
    FORM: 'ezart_builder_form',
    MODEL: 'ezart_builder_model',
    SHOW_RESUME_OR_NEW: 'ezart_builder_show_resume_or_new',
    ACTIVE_CHARACTER_ID: 'ezart_builder_active_character_id',
    // New builder phase persistence keys
    BUILDER_PHASE: 'ezart_builder_phase',
    PROMPT_BUILDER_DATA: 'ezart_prompt_builder_data',
    DUAL_REVIEW_DATA: 'ezart_dual_review_data',
    PENDING_CHARACTER_ID: 'ezart_pending_character_id',
    CHARACTER_NAME: 'ezart_character_name'
  };

  // Helper: Save state to URL query params for persistence across refreshes
  const updateUrlState = (state: {
    projectId?: string;
    archetype?: string;
    form?: string;
    model?: string;
    view?: string;
    characterId?: string;
    phase?: string;
  }) => {
    const params = new URLSearchParams();
    if (state.projectId) params.set('project', state.projectId);
    if (state.archetype) params.set('archetype', state.archetype);
    if (state.form) params.set('form', state.form);
    if (state.model) params.set('model', state.model);
    if (state.view) params.set('view', state.view);
    if (state.characterId) params.set('characterId', state.characterId);
    if (state.phase) params.set('phase', state.phase);

    const newUrl = params.toString() ? `/character?${params.toString()}` : '/character';
    navigate(newUrl, { replace: true });
  };

  // Helper: Read state from URL query params
  const readUrlState = () => {
    const params = new URLSearchParams(location.search);

    return {
      projectId: params.get('project') || undefined,
      archetype: params.get('archetype') as CharacterArchetype | undefined,
      builderPhase: params.get('phase') as BuilderPhase | undefined,
      form: params.get('form') as CharacterForm | undefined,
      model: params.get('model') as 'official' | 'testing' | undefined,
      view: params.get('view') || undefined,
      characterId: params.get('characterId') || undefined,
    };
  };

  // NOTE: Builder state is persisted to Supabase via character.builder_session
  // No localStorage persistence for builder phase data

  // Clear builder phase state (for starting fresh)
  const clearBuilderPhaseData = () => {
    // Clear any stale localStorage keys (legacy cleanup)
    localStorage.removeItem(KEYS.BUILDER_PHASE);
    localStorage.removeItem(KEYS.PROMPT_BUILDER_DATA);
    localStorage.removeItem(KEYS.DUAL_REVIEW_DATA);
    localStorage.removeItem(KEYS.PENDING_CHARACTER_ID);
    localStorage.removeItem(KEYS.CHARACTER_NAME);
    localStorage.removeItem('ezart_prompt_builder_local');
    // Clear in-memory state
    setBuilderPhase(null);
    setPromptBuilderData(null);
    setDualReviewData(null);
  };

  // 1. Initial Load: Projects + Session Recovery
  useEffect(() => {
    const init = async () => {
      if (!user) return;

      // Skip navigation logic if already initialized (prevents re-navigation on auth state changes)
      const shouldSkipNavigation = hasInitialized.current;

      try {
        // Fetch both owned and joined projects
        const [ownedProjects, joinedProjects] = await Promise.all([
          ProjectService.getProjects(),
          import('../lib/projectMemberService').then(m => m.ProjectMemberService.getJoinedProjects())
        ]);

        // Combine owned and joined projects
        const allProjects = [...ownedProjects, ...joinedProjects];

        setProjects(allProjects);

        // Skip the rest if already initialized - just refresh projects list
        if (shouldSkipNavigation) {
          setIsLoading(false);
          return;
        }
        hasInitialized.current = true;

        // First priority: Restore from URL hash (survives page refresh)
        const urlState = readUrlState();

        // Second priority: Restore from LocalStorage, then context, then prop
        const sProjectId = urlState?.projectId || localStorage.getItem(KEYS.PROJECT) || selectedProjectId || initialProjectId;
        const sArchetype = urlState?.archetype || localStorage.getItem(KEYS.ARCHETYPE) as CharacterArchetype;
        const sForm = urlState?.form || localStorage.getItem(KEYS.FORM) as CharacterForm;
        const sModel = urlState?.model || localStorage.getItem(KEYS.MODEL) as 'official' | 'testing';
        const sShowResumeOrNew = urlState?.view === 'archetype' || localStorage.getItem(KEYS.SHOW_RESUME_OR_NEW) === 'true';
        const sActiveCharacterId = urlState?.characterId || localStorage.getItem(KEYS.ACTIVE_CHARACTER_ID);

        // Priority 0: If URL says view=profile, just show the profile page
        if (urlState?.view === 'profile' && urlState?.characterId && sProjectId) {
          console.log('📋 Restoring character profile view from URL:', urlState.characterId);
          setSelectedProjectId(sProjectId);
          setShowProjectSelector(false);
          setShowCharacterProfile(urlState.characterId);
          return; // Don't continue with workflow restoration
        }

        // NOTE: Builder phase is restored from character.builder_session in Supabase
        // See handleResumeCharacterCreation for restoration logic

        // Restore workflow state based on what we have available
        // IMPORTANT: Only auto-resume if URL explicitly has the workflow params
        // This prevents auto-resuming when user just navigates to /character
        const urlHasWorkflowState = urlState?.archetype && urlState?.model;
        const urlHasCharacterId = urlState?.characterId;

        const hasActiveCharacter = urlHasCharacterId && sActiveCharacterId && loadSession(sActiveCharacterId);
        const hasCompleteWorkflow = sProjectId && sArchetype && sModel && urlHasWorkflowState;
        const hasPartialWorkflow = sProjectId && (sArchetype || sModel) && urlHasWorkflowState;

        if (hasActiveCharacter && userProjects.some(p => p.id === sProjectId)) {
          // Priority 1: Restore active character session (only if URL has characterId)
          console.log('🔄 Restoring active character session:', sActiveCharacterId);
          setSelectedProjectId(sProjectId);
          setShowProjectSelector(false);
          setSelectedArchetype(sArchetype || 'film');
          setSelectedForm(sForm || 'humanoid');
          setSelectedModel(sModel || 'official');

          // URL already has the state, no need to update
        } else if (hasCompleteWorkflow && userProjects.some(p => p.id === sProjectId)) {
          // Priority 2: Full workflow restore - user was in the middle of character creation
          setSelectedProjectId(sProjectId);
          setShowProjectSelector(false);
          setSelectedArchetype(sArchetype);
          setSelectedForm(sForm || 'humanoid');
          setSelectedModel(sModel);

          if (sShowResumeOrNew) {
            setShowResumeOrNew(true);
          }

          // Update URL to match restored state
          updateUrlState({
            projectId: sProjectId,
            archetype: sArchetype,
            form: sForm || undefined,
            model: sModel,
            view: sShowResumeOrNew ? 'archetype' : undefined,
          });
        } else if (hasPartialWorkflow && userProjects.some(p => p.id === sProjectId)) {
          // Priority 3: Partial workflow - restore what we have
          setSelectedProjectId(sProjectId);
          setShowProjectSelector(false);

          if (sArchetype) {
            setSelectedArchetype(sArchetype);
            setSelectedForm(sForm || 'humanoid');
          }

          if (sModel) {
            setSelectedModel(sModel);
          }

          if (sShowResumeOrNew) {
            setShowResumeOrNew(true);
          }

          // Update URL to match restored state
          updateUrlState({
            projectId: sProjectId,
            archetype: sArchetype || undefined,
            form: sForm || undefined,
            model: sModel || undefined,
            view: sShowResumeOrNew ? 'archetype' : undefined,
          });
        } else if (initialProjectId && userProjects.some(p => p.id === initialProjectId)) {
          // Direct navigation with initialProjectId prop
          setSelectedProjectId(initialProjectId);
          setShowProjectSelector(false);
          updateUrlState({ projectId: initialProjectId });
        } else if (sProjectId && userProjects.some(p => p.id === sProjectId)) {
          // Just have a valid project from context - use it without workflow state
          setSelectedProjectId(sProjectId);
          setShowProjectSelector(false);
          updateUrlState({ projectId: sProjectId });
        } else {
          // No valid state to restore - clear stale localStorage and show project selector
          if (sProjectId || sArchetype || sModel) {
            console.log('🧹 Clearing stale character builder state');
            Object.values(KEYS).forEach(k => localStorage.removeItem(k));
          }
          setShowProjectSelector(true);
        }
      } catch (err) {
        showToast('Failed to load projects', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [user, initialProjectId, showToast]);

  // 2. Fetch Existing Character and Ongoing Sessions when Project is selected
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!selectedProjectId) return;

      try {
        // Fetch all characters from database for this project (both draft and finalized)
        const projectCharacters = await getCharactersByProjectId(selectedProjectId);

        // Merge localStorage session data with DB characters to show uncommitted layer images
        const mergedCharacters = projectCharacters.map(char => {
          const session = loadSession(char.id);

          // If there's a localStorage session with more recent data, merge layer images
          if (session && session.lastSaved > (char.updated_at || 0)) {
            const mergedCoreLayers = { ...char.core_layers };

            if (session.layerStates[1]?.currentUrl && !mergedCoreLayers.body) {
              mergedCoreLayers.body = {
                id: `${char.id}_body`,
                type: 'body',
                step: 1,
                source: 'generated',
                source_url: session.layerStates[1].currentUrl,
                rendered_url: session.layerStates[1].committedUrl || session.layerStates[1].currentUrl,
                edits: [],
                created_at: session.layerStates[1].lastUpdated,
                modified_at: session.layerStates[1].lastUpdated,
              };
            }

            if (session.layerStates[2]?.currentUrl && !mergedCoreLayers.head) {
              mergedCoreLayers.head = {
                id: `${char.id}_head`,
                type: 'head',
                step: 2,
                source: 'generated',
                source_url: session.layerStates[2].currentUrl,
                rendered_url: session.layerStates[2].committedUrl || session.layerStates[2].currentUrl,
                edits: [],
                created_at: session.layerStates[2].lastUpdated,
                modified_at: session.layerStates[2].lastUpdated,
              };
            }

            return { ...char, core_layers: mergedCoreLayers };
          }

          return char;
        });

        // Show all characters (drafts and finalized) - users can work on incomplete characters
        setCompletedCharacters(mergedCharacters);

        // Set existing character if not already set (for editing)
        // BUT don't auto-set if user is creating a new character or has backed out
        if (!existingCharacter && !isCreatingNew && !hasBackedOut.current && mergedCharacters.length > 0) {
          const latest = mergedCharacters[0];
          setExistingCharacter(latest);
        }

      } catch (err) {
        console.warn('Failed to fetch project data:', err);
      }
    };
    fetchProjectData();
  }, [selectedProjectId, refetchTrigger]);

  // Fetch voice data for all characters (batched - single query)
  useEffect(() => {
    if (completedCharacters.length === 0) return;

    const fetchVoiceData = async () => {
      const voiceMap: Record<string, { hasVoice: boolean; audioUrl: string | null }> = {};
      const characterIds = completedCharacters.map(c => c.id);

      try {
        // Single batched query for all character voices
        const voicesMap = await getVoicesByCharacterIds(characterIds);

        completedCharacters.forEach((char) => {
          const voice = voicesMap.get(char.id);
          if (voice) {
            // Check saved_samples first (preferred)
            const samples = (voice as any).saved_samples as SavedVoiceSample[] | undefined;
            if (samples && samples.length > 0) {
              const defaultSample = samples.find(s => s.is_default) || samples[0];
              voiceMap[char.id] = { hasVoice: true, audioUrl: defaultSample.audio_url };
            } else if (voice.preview_audio_url) {
              // Fallback to preview_audio_url
              voiceMap[char.id] = { hasVoice: true, audioUrl: voice.preview_audio_url };
            } else {
              voiceMap[char.id] = { hasVoice: false, audioUrl: null };
            }
          } else {
            voiceMap[char.id] = { hasVoice: false, audioUrl: null };
          }
        });
      } catch (err) {
        // On error, mark all as no voice
        completedCharacters.forEach((char) => {
          voiceMap[char.id] = { hasVoice: false, audioUrl: null };
        });
      }

      setCharacterVoices(voiceMap);
    };

    fetchVoiceData();
  }, [completedCharacters]);

  // Handle audio playback
  const handlePlayVoice = (e: React.MouseEvent, characterId: string) => {
    e.stopPropagation(); // Prevent card click

    const voiceData = characterVoices[characterId];
    if (!voiceData?.hasVoice || !voiceData.audioUrl) return;

    // If already playing this character, stop it
    if (playingAudioId === characterId && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingAudioId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Create new audio element and play
    const audio = new Audio(voiceData.audioUrl);
    audioRef.current = audio;

    audio.onended = () => {
      setPlayingAudioId(null);
    };

    audio.onerror = () => {
      console.error('Failed to play audio');
      setPlayingAudioId(null);
    };

    audio.play().then(() => {
      setPlayingAudioId(characterId);
    }).catch(err => {
      console.error('Audio playback failed:', err);
    });
  };

  // Handle character deletion
  const handleDeleteCharacter = async () => {
    if (!deleteConfirmCharacter) return;

    setIsDeleting(true);
    try {
      // Delete voice data first
      await deleteVoiceByCharacterId(deleteConfirmCharacter.id);
      // Delete the character
      await deleteCharacter2D(deleteConfirmCharacter.id);

      // Update local state
      setCompletedCharacters(prev => prev.filter(c => c.id !== deleteConfirmCharacter.id));
      setCharacterVoices(prev => {
        const updated = { ...prev };
        delete updated[deleteConfirmCharacter.id];
        return updated;
      });

      showToast('Character deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete character:', error);
      showToast('Failed to delete character', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmCharacter(null);
    }
  };

  // Handle character name edit
  const handleStartEditName = (e: React.MouseEvent, character: Character2DLayered) => {
    e.stopPropagation(); // Prevent card click
    setEditingCharacterId(character.id);
    setEditingCharacterName(character.name || '');
  };

  const handleSaveCharacterName = async (characterId: string) => {
    if (!editingCharacterName.trim()) {
      showToast('Character name cannot be empty', 'error');
      return;
    }

    try {
      const character = completedCharacters.find(c => c.id === characterId);
      if (!character) return;

      const updatedCharacter = { ...character, name: editingCharacterName.trim() };
      await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId! });

      // Update local state
      setCompletedCharacters(prev =>
        prev.map(c => c.id === characterId ? updatedCharacter : c)
      );

      showToast('Character name updated', 'success');
    } catch (error) {
      console.error('Failed to update character name:', error);
      showToast('Failed to update character name', 'error');
    } finally {
      setEditingCharacterId(null);
      setEditingCharacterName('');
    }
  };

  const handleCancelEditName = () => {
    setEditingCharacterId(null);
    setEditingCharacterName('');
  };

  // Refs for trash bin hit detection
  const trashBinRef = useRef<HTMLDivElement>(null);
  const isOverTrashRef = useRef(false); // Ref to track trash hover without re-registering listeners

  // Track pending drag (before threshold is met)
  const pendingDragRef = useRef<{
    characterId: string;
    startX: number;
    startY: number;
    cardRect: { left: number; top: number };
  } | null>(null);

  // Track if a drag just ended (to prevent click from firing)
  const justDraggedRef = useRef(false);

  // Mouse-based drag handlers (more reliable than HTML5 drag API)
  const handleMouseDown = (e: React.MouseEvent, characterId: string) => {
    // Only left click
    if (e.button !== 0) return;

    // Store pending drag info - don't start drag until movement threshold is met
    if (e.currentTarget instanceof HTMLElement) {
      const rect = e.currentTarget.getBoundingClientRect();
      pendingDragRef.current = {
        characterId,
        startX: e.clientX,
        startY: e.clientY,
        cardRect: { left: rect.left, top: rect.top },
      };
    }
  };

  // Global mouse move/up handlers for drag
  useEffect(() => {
    const DRAG_THRESHOLD = 5; // pixels before drag starts

    const handleMouseMove = (e: MouseEvent) => {
      // Check if we should start dragging (threshold met)
      if (pendingDragRef.current && !draggingCharacterId) {
        const dx = e.clientX - pendingDragRef.current.startX;
        const dy = e.clientY - pendingDragRef.current.startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance >= DRAG_THRESHOLD) {
          // Start actual drag
          setDraggingCharacterId(pendingDragRef.current.characterId);
          setDragStartRect(pendingDragRef.current.cardRect);
          setDragPosition({ x: e.clientX, y: e.clientY });
        }
        return;
      }

      // If already dragging, update position
      if (draggingCharacterId) {
        setDragPosition({ x: e.clientX, y: e.clientY });

        // Check if over trash bin
        if (trashBinRef.current) {
          const trashRect = trashBinRef.current.getBoundingClientRect();
          const isOver = (
            e.clientX >= trashRect.left &&
            e.clientX <= trashRect.right &&
            e.clientY >= trashRect.top &&
            e.clientY <= trashRect.bottom
          );
          setIsOverTrash(isOver);
          isOverTrashRef.current = isOver; // Keep ref in sync for mouseup handler
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Clear pending drag
      pendingDragRef.current = null;

      // If we were dragging, mark it to prevent click handler
      if (draggingCharacterId) {
        justDraggedRef.current = true;
        // Clear the flag after click event would have fired
        setTimeout(() => {
          justDraggedRef.current = false;
        }, 0);
      }

      // Check if dropped on trash (only if actually dragging)
      // Use ref to avoid stale closure issue from effect re-registration
      if (isOverTrashRef.current && draggingCharacterId) {
        const character = completedCharacters.find(c => c.id === draggingCharacterId);
        if (character) {
          setDeleteConfirmCharacter(character);
        }
      }

      setDraggingCharacterId(null);
      setIsOverTrash(false);
      isOverTrashRef.current = false;
      setDragPosition(null);
      setDragStartRect(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingCharacterId, completedCharacters]); // Removed isOverTrash - using ref instead

  // 3. Auto-resume character session on page load if characterId is in URL/localStorage
  // This effect ONLY runs when user explicitly has full workflow state in URL
  useEffect(() => {
    if (!selectedProjectId) return;

    // Skip if user deliberately backed out - don't auto-resume
    if (hasBackedOut.current) {
      console.log('🚫 Skipping auto-resume: user backed out');
      return;
    }

    // Skip if we've already attempted to resume for this project
    if (hasAttemptedResume.current === selectedProjectId) {
      return;
    }

    // Skip if we already have a valid character with core layers
    if (existingCharacter && existingCharacter.core_layers &&
      (existingCharacter.core_layers.body || existingCharacter.core_layers.head)) {
      hasAttemptedResume.current = selectedProjectId;
      return;
    }

    const resumeCharacter = async () => {
      const urlState = readUrlState();

      // CRITICAL: Only auto-resume if URL has complete workflow state
      // This prevents auto-resuming when user is just browsing "My Characters"
      const hasCharacterIdInUrl = !!urlState?.characterId;

      // Set loading state if we're about to load character data
      if (hasCharacterIdInUrl) {
        setIsLoadingCharacterData(true);
      }
      const hasCompleteUrlState = urlState?.characterId && urlState?.archetype && urlState?.model;

      if (!hasCompleteUrlState) {
        // User is browsing (not in active builder session) - don't auto-resume
        console.log('🚫 Skipping auto-resume: URL does not have complete workflow state');
        hasAttemptedResume.current = selectedProjectId;
        setIsLoadingCharacterData(false);
        return;
      }

      const characterId = urlState.characterId;

      if (!characterId) {
        // No character ID in URL/storage, mark as attempted and clear
        hasAttemptedResume.current = selectedProjectId;
        if (existingCharacter) {
          console.log('🧹 Clearing invalid character without ID');
          setExistingCharacter(null);
        }
        setIsLoadingCharacterData(false);
        return;
      }

      // Priority 1: Try to load from Supabase (source of truth - has R2 URLs)
      try {
        const savedCharacter = await getCharacterById(characterId);
        if (savedCharacter && savedCharacter.project_id === selectedProjectId) {
          // Check if character has layers OR viewport state (draft images in progress)
          const hasLayers = savedCharacter.core_layers &&
            (savedCharacter.core_layers.body || savedCharacter.core_layers.head);
          const hasViewportState = savedCharacter.builder_session?.viewport_state &&
            (savedCharacter.builder_session.viewport_state.bodyImageUrl ||
              savedCharacter.builder_session.viewport_state.headImageUrl);
          const hasBuilderData = savedCharacter.builder_session?.prompt_data ||
            savedCharacter.builder_session?.review_data;

          // Character is valid if it has finalized layers, viewport images in progress, or builder data
          if (!hasLayers && !hasViewportState && !hasBuilderData) {
            console.log('⚠️ Character in DB has no layers, viewport state, or builder data - clearing session');
            localStorage.removeItem(KEYS.ACTIVE_CHARACTER_ID);
            hasAttemptedResume.current = selectedProjectId;
            setExistingCharacter(null);
            setIsLoadingCharacterData(false);
            return;
          }

          const isDraft = savedCharacter.status === 'draft' || !savedCharacter.is_finalized;
          console.log(`✅ Loaded ${isDraft ? 'draft' : 'published'} character from Supabase:`, characterId);
          hasAttemptedResume.current = selectedProjectId;
          setExistingCharacter(savedCharacter);

          // Workflow state should already be set from URL by init effect
          // Just ensure we have values if somehow missing
          if (!selectedArchetype) setSelectedArchetype(urlState.archetype || savedCharacter.archetype || 'film');
          if (!selectedForm) setSelectedForm(urlState.form as CharacterForm || savedCharacter.form || 'humanoid');
          if (!selectedModel) setSelectedModel(urlState.model || 'official');
          setIsLoadingCharacterData(false);
          return;
        }
      } catch (err) {
        console.warn('Failed to load character from Supabase, falling back to localStorage:', err);
      }

      // Priority 2: Fallback to localStorage session (may have temporary URLs)
      const session = loadSession(characterId);
      if (session && session.projectId === selectedProjectId) {
        console.log('🔄 Auto-resuming character from localStorage session:', characterId);

        // Convert LayerImageState back to LayerData for each completed layer
        const createLayerDataFromState = (layerState: any, type: 'body' | 'head'): any => {
          if (!layerState) return null;

          return {
            id: `${session.characterId}_${type}`,
            type,
            step: layerState.step,
            source: 'generated',
            source_url: layerState.currentUrl,
            rendered_url: layerState.committedUrl || layerState.currentUrl,
            edits: [],
            created_at: Date.now(),
            modified_at: layerState.lastUpdated,
          };
        };

        // Reconstruct character from session
        const reconstructedCharacter: Character2DLayered = {
          id: session.characterId,
          user_id: session.userId,
          project_id: session.projectId,
          name: 'Resumed Character',
          archetype: (urlState.archetype || selectedArchetype || 'film') as CharacterArchetype,
          form: (urlState.form || selectedForm || 'humanoid') as CharacterForm,
          style_preset: getStylePresetForArchetype((urlState.archetype || selectedArchetype || 'film') as CharacterArchetype),
          core_layers: {
            body: createLayerDataFromState(session.layerStates[1], 'body'),
            head: createLayerDataFromState(session.layerStates[2], 'head'),
          },
          current_step: session.currentStep,
          completed_steps: session.completedSteps,
          is_finalized: session.isFinalized,
          character_type: '2d_layered',
          created_at: Date.now(),
          updated_at: Date.now(),
        };

        // Set the existing character to the reconstructed one
        hasAttemptedResume.current = selectedProjectId;
        setExistingCharacter(reconstructedCharacter);

        // Set workflow state if not already set
        if (!selectedArchetype) setSelectedArchetype('film');
        if (!selectedForm) setSelectedForm('humanoid');
        if (!selectedModel) setSelectedModel('official');
        setIsLoadingCharacterData(false);
      } else {
        // No valid session found, clear the character ID
        console.log('❌ No valid session found for character:', characterId);
        localStorage.removeItem(KEYS.ACTIVE_CHARACTER_ID);
        hasAttemptedResume.current = selectedProjectId;
        setExistingCharacter(null);
        setIsLoadingCharacterData(false);
      }
    };

    resumeCharacter();
  }, [selectedProjectId, selectedArchetype, selectedModel, showResumeOrNew]);

  // Restore builder session data when existingCharacter loads (for page refresh resume)
  useEffect(() => {
    if (!existingCharacter?.builder_session) return;

    const builderSession = existingCharacter.builder_session;

    // Restore dualReviewData if missing and character has review_data
    if (!dualReviewData && builderSession.review_data) {
      console.log('📋 Restoring dualReviewData from builder_session');
      setDualReviewData(builderSession.review_data);
    }

    // Restore promptBuilderData if missing and character has prompt_data
    if (!promptBuilderData && builderSession.prompt_data) {
      console.log('📋 Restoring promptBuilderData from builder_session');
      setPromptBuilderData(builderSession.prompt_data);
    }

    // NOTE: We do NOT auto-restore builderPhase here.
    // The user must explicitly click "Resume" on a character to continue a session.
    // builder_session is saved for resume capability only - restored via handleResumeCharacterCreation.
  }, [existingCharacter?.builder_session, dualReviewData, promptBuilderData]);

  // Sync state to LocalStorage and URL whenever any state changes
  useEffect(() => {
    // Update localStorage
    if (selectedProjectId) localStorage.setItem(KEYS.PROJECT, selectedProjectId);
    else localStorage.removeItem(KEYS.PROJECT);

    if (selectedArchetype) localStorage.setItem(KEYS.ARCHETYPE, selectedArchetype);
    else localStorage.removeItem(KEYS.ARCHETYPE);

    if (selectedForm) localStorage.setItem(KEYS.FORM, selectedForm);
    else localStorage.removeItem(KEYS.FORM);

    if (selectedModel) localStorage.setItem(KEYS.MODEL, selectedModel);
    else localStorage.removeItem(KEYS.MODEL);

    if (showResumeOrNew) localStorage.setItem(KEYS.SHOW_RESUME_OR_NEW, 'true');
    else localStorage.removeItem(KEYS.SHOW_RESUME_OR_NEW);

    if (existingCharacter?.id) localStorage.setItem(KEYS.ACTIVE_CHARACTER_ID, existingCharacter.id);
    else localStorage.removeItem(KEYS.ACTIVE_CHARACTER_ID);

    // Update URL hash to persist across refreshes
    // ONLY update URL when we have actual workflow state (archetype AND model)
    // This prevents overwriting the URL when user backs out to My Characters page
    if (selectedProjectId && selectedArchetype && selectedModel) {
      updateUrlState({
        projectId: selectedProjectId,
        archetype: selectedArchetype,
        form: selectedForm || undefined,
        model: selectedModel,
        view: showResumeOrNew ? 'archetype' : undefined,
        characterId: existingCharacter?.id || undefined,
        phase: builderPhase || undefined,
      });
    }
  }, [selectedProjectId, selectedArchetype, selectedForm, selectedModel, showResumeOrNew, existingCharacter?.id, builderPhase]);


  const handleCharacterSave = async (character: Character2DLayered, passport?: CharacterPassport) => {
    if (!selectedProjectId) {
      showToast('No project selected', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const characterWithProject = { ...character, project_id: selectedProjectId };
      await saveCharacter2D(characterWithProject);

      if (passport) {
        await saveCharacterPassport(passport);
        showToast('Character and passport saved successfully!', 'success');
      } else {
        showToast('Character saved successfully!', 'success');
      }

      // NOTE: We no longer auto-navigate away after finalization
      // The CharacterBuilder2D shows a completion screen with the reference sheet
      // The user can click "View Character Page" when ready to navigate
    } catch (err) {
      console.error('Save error:', err);
      showToast('Failed to save character', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const clearSession = () => {
    Object.values(KEYS).forEach(k => localStorage.removeItem(k));
    hasAttemptedResume.current = null;
    setSelectedProjectId(null);
    setSelectedArchetype(null);
    setSelectedModel(null);
    setExistingCharacter(null);
    setShowResumeOrNew(false);
    setIsCreatingNew(false);
    setCharacterName('');
    // Navigate back to Studio
    navigate('/');
  };

  // Handler for resuming character creation from character profile
  const handleResumeCharacterCreation = async (characterId: string) => {
    // Reset backed out flag since user is actively resuming
    hasBackedOut.current = false;
    // Load the character and go to the builder
    setShowCharacterProfile(null);

    try {
      // First, load from Supabase (source of truth for saved data)
      const dbCharacter = await getCharacterById(characterId);

      // Also check localStorage for any unsaved work-in-progress
      const session = loadSession(characterId);

      if (dbCharacter) {
        let characterToResume = dbCharacter;

        // If there's a localStorage session with more recent data, merge it
        if (session && session.lastSaved > (dbCharacter.updated_at || 0)) {
          console.log('🔄 Merging localStorage session with DB character - session is newer');

          // Merge layer states from session into character
          const mergedCoreLayers = { ...dbCharacter.core_layers };

          // Check each layer - if session has uncommitted work, use session's URL
          if (session.layerStates[1]?.currentUrl && !mergedCoreLayers.body) {
            mergedCoreLayers.body = {
              id: `${characterId}_body`,
              type: 'body',
              step: 1,
              source: 'generated',
              source_url: session.layerStates[1].currentUrl,
              rendered_url: session.layerStates[1].committedUrl || session.layerStates[1].currentUrl,
              edits: [],
              created_at: session.layerStates[1].lastUpdated,
              modified_at: session.layerStates[1].lastUpdated,
            };
          }

          if (session.layerStates[2]?.currentUrl && !mergedCoreLayers.head) {
            mergedCoreLayers.head = {
              id: `${characterId}_head`,
              type: 'head',
              step: 2,
              source: 'generated',
              source_url: session.layerStates[2].currentUrl,
              rendered_url: session.layerStates[2].committedUrl || session.layerStates[2].currentUrl,
              edits: [],
              created_at: session.layerStates[2].lastUpdated,
              modified_at: session.layerStates[2].lastUpdated,
            };
          }

          characterToResume = {
            ...dbCharacter,
            core_layers: mergedCoreLayers,
            current_step: session.currentStep || dbCharacter.current_step,
            completed_steps: session.completedSteps.length > dbCharacter.completed_steps.length
              ? session.completedSteps
              : dbCharacter.completed_steps,
          };
        }

        // Ensure we have valid archetype/form - use defaults if not set
        const archetypeToUse = characterToResume.archetype || 'film';
        const formToUse = characterToResume.form || 'humanoid';

        console.log('▶️ Resuming character creation:', {
          id: characterToResume.id,
          archetype: archetypeToUse,
          form: formToUse,
          hasBody: !!characterToResume.core_layers?.body,
        });

        setExistingCharacter(characterToResume);
        setSelectedArchetype(archetypeToUse);
        setSelectedForm(formToUse);
        setCharacterName(characterToResume.name);

        // Restore builder session if present
        const builderSession = characterToResume.builder_session;
        if (builderSession) {
          const modelToUse = builderSession.model || 'official';
          setSelectedModel(modelToUse);

          // Restore phase-specific data
          if (builderSession.prompt_data) {
            setPromptBuilderData(builderSession.prompt_data);
          }
          if (builderSession.review_data) {
            setDualReviewData(builderSession.review_data);
          }

          // Determine valid phase based on available data
          // Fall back to earlier phase if required data is missing
          let phaseToResume = builderSession.phase;
          if (phaseToResume === 'reference-sheet-preview') {
            // reference-sheet-preview requires reference_sheet_url and passport_data
            if (builderSession.reference_sheet_url && builderSession.passport_data) {
              // Restore the reference sheet preview state
              setReferenceSheetUrl(builderSession.reference_sheet_url);
              setReferenceSheetUnclothedUrl(builderSession.reference_sheet_unclothed_url || null);
              setReferenceSheetVersion(builderSession.reference_sheet_unclothed_url && builderSession.reference_sheet_version === 'unclothed' ? 'unclothed' : 'original');
              setPendingCharacterData(characterToResume);
              setPendingPassportData(builderSession.passport_data);
              console.log('✅ Restoring reference-sheet-preview with saved reference sheet');
            } else {
              // Missing reference sheet data - fall back to dual-viewport to regenerate
              phaseToResume = builderSession.feature_editor ? 'feature-editor' : 'dual-viewport';
              console.log('⚠️ Missing reference_sheet_url for reference-sheet-preview, falling back to dual-viewport');
            }
          } else if ((phaseToResume === 'dual-viewport' || phaseToResume === 'feature-editor') && !builderSession.review_data) {
            // dual-viewport requires review_data - fall back
            if (builderSession.prompt_data) {
              phaseToResume = 'dual-review';
              console.log('⚠️ Missing review_data for dual-viewport, falling back to dual-review');
            } else {
              phaseToResume = 'prompt-builder';
              console.log('⚠️ Missing review_data for dual-viewport, falling back to prompt-builder');
            }
          } else if (phaseToResume === 'dual-review' && !builderSession.prompt_data) {
            // dual-review requires prompt_data - fall back
            phaseToResume = 'prompt-builder';
            console.log('⚠️ Missing prompt_data for dual-review, falling back to prompt-builder');
          }

          // Set the phase to resume from
          setBuilderPhase(phaseToResume);
          setShowResumeOrNew(true);

          console.log('▶️ Restoring builder session:', {
            phase: builderSession.phase,
            actualPhase: phaseToResume,
            model: modelToUse,
            hasPromptData: !!builderSession.prompt_data,
            hasReviewData: !!builderSession.review_data,
            hasReferenceSheet: !!builderSession.reference_sheet_url,
          });
        } else {
          // No builder session - start fresh from prompt builder
          setSelectedModel('official');
          setBuilderPhase('prompt-builder');
          setShowResumeOrNew(true);
        }

        setInitialShowCompositor(session?.showCompositor || false);
        localStorage.setItem(KEYS.ACTIVE_CHARACTER_ID, characterToResume.id);

        // Update URL to builder view (clears profile view)
        updateUrlState({
          projectId: characterToResume.project_id,
          archetype: archetypeToUse,
          form: formToUse,
          model: builderSession?.model || 'official',
          characterId: characterToResume.id,
          phase: builderSession?.phase,
        });
      } else if (session) {
        // No DB character but we have a session - reconstruct from session
        console.log('🔄 Reconstructing character from localStorage session (no DB record)');

        const createLayerDataFromState = (layerState: any, type: 'body' | 'head'): any => {
          if (!layerState?.currentUrl) return null;

          return {
            id: `${characterId}_${type}`,
            type,
            step: type === 'body' ? 1 : 2,
            source: 'generated',
            source_url: layerState.currentUrl,
            rendered_url: layerState.committedUrl || layerState.currentUrl,
            edits: [],
            created_at: layerState.lastUpdated,
            modified_at: layerState.lastUpdated,
          };
        };

        const reconstructedCharacter: Character2DLayered = {
          id: session.characterId,
          user_id: session.userId,
          project_id: session.projectId,
          name: session.characterName || 'Resumed Character',
          archetype: 'film', // Default - will be set by workflow state
          form: 'humanoid',
          style_preset: getStylePresetForArchetype('film'),
          core_layers: {
            body: createLayerDataFromState(session.layerStates[1], 'body'),
            head: createLayerDataFromState(session.layerStates[2], 'head'),
          },
          current_step: session.currentStep,
          completed_steps: session.completedSteps,
          is_finalized: session.isFinalized,
          character_type: '2d_layered',
          created_at: Date.now(),
          updated_at: session.lastSaved,
        };

        console.log('▶️ Resuming character from session:', {
          id: characterId,
          archetype: 'film',
          form: 'humanoid',
          hasBody: !!reconstructedCharacter.core_layers?.body,
        });

        setExistingCharacter(reconstructedCharacter);
        setSelectedArchetype('film');
        setSelectedForm('humanoid');
        setSelectedModel('official');
        setShowResumeOrNew(true); // Skip the "My Characters" page and go straight to builder
        setInitialShowCompositor(session.showCompositor || false); // Restore compositor view if active
        localStorage.setItem(KEYS.ACTIVE_CHARACTER_ID, characterId);

        // Update URL to builder view (clears profile view)
        updateUrlState({
          projectId: session.projectId,
          archetype: 'film',
          form: 'humanoid',
          model: 'official',
          characterId: characterId,
        });
      } else {
        showToast('Character not found', 'error');
      }
    } catch (err) {
      console.error('Failed to load character:', err);
      showToast('Failed to load character', 'error');
    }
  };

  // Handler for character name submission - creates character in DB immediately
  const handleNameSubmit = async (name: string) => {
    if (!user || !selectedProjectId) {
      showToast('Please select a project first', 'error');
      return;
    }

    // Reset backed out flag since user is actively creating a new character
    hasBackedOut.current = false;

    // IMPORTANT: Clear ALL old builder state before starting fresh
    clearBuilderPhaseData();
    setDualViewportResult(null);
    setSelectedArchetype(null);
    setSelectedForm('humanoid');

    // Generate a fresh character ID for this new character
    const newCharId = generateId();
    setPendingCharacterId(newCharId);
    localStorage.setItem('ezart_pending_character_id', newCharId);

    setCharacterName(name);
    setShowNameModal(false);

    // Clear existing character since we're creating a NEW character
    setExistingCharacter(null);
    setIsCreatingNew(true);

    // Create a minimal character record in the database immediately
    // This ensures the character exists even if user navigates away
    const newCharacter: Character2DLayered = {
      id: newCharId,
      user_id: user.id,
      project_id: selectedProjectId,
      name: name,
      archetype: 'film', // Default, will be updated when selected in PromptBuilder
      form: 'humanoid', // Default, auto-detected by Gemini now
      style_preset: 'photorealistic',
      core_layers: {},
      current_step: 1,
      completed_steps: [],
      is_finalized: false,
      character_type: '2d_layered',
      created_at: Date.now(),
      updated_at: Date.now(),
    };

    try {
      await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
      setExistingCharacter(newCharacter);
      showToast(`Character "${name}" created!`, 'success');
    } catch (error) {
      console.error('Failed to create character:', error);
      showToast('Failed to create character', 'error');
      return;
    }

    // Go directly to prompt-builder phase (skip archetype/model selection screens)
    setSelectedModel('official'); // Default to official model
    setBuilderPhase('prompt-builder');
  };

  // Clear stale showResumeOrNew state on refresh
  // This prevents infinite loading when localStorage has stale state but no builder phase is active
  useEffect(() => {
    if (showResumeOrNew && !builderPhase && !isLoading && !isLoadingCharacterData) {
      console.log('🧹 Clearing stale showResumeOrNew - no builder phase active');
      setShowResumeOrNew(false);
      localStorage.removeItem(KEYS.SHOW_RESUME_OR_NEW);
    }
  }, [showResumeOrNew, builderPhase, isLoading, isLoadingCharacterData]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-carbon">
        <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
      </div>
    );
  }

  // Show Character Profile if a character is selected
  if (showCharacterProfile) {
    return (
      <CharacterProfilePage
        characterId={showCharacterProfile}
        onBack={() => {
          setShowCharacterProfile(null);
          // Trigger refetch to show updated character data (e.g., edited name)
          setRefetchTrigger(prev => prev + 1);
          // Clear profile view from URL, return to My Characters page
          updateUrlState({
            projectId: selectedProjectId || undefined,
          });
        }}
        onResumeSession={handleResumeCharacterCreation}
      />
    );
  }

  // FIRST: Show project selector if no project is selected
  // This must be checked BEFORE any other step in the character creation flow
  if (!selectedProjectId || showProjectSelector) {
    // Calculate pagination
    const totalPages = Math.ceil(projects.length / projectsPerPage);
    const startIndex = (currentPage - 1) * projectsPerPage;
    const endIndex = startIndex + projectsPerPage;
    const currentProjects = projects.slice(startIndex, endIndex);

    return (
      <div className="flex-1 w-full flex flex-col bg-carbon text-white">
        <ProductionBar page="character" />

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-2xl w-full">
            <div className="text-center mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
              <h1 className="text-4xl font-bold mb-4">Select a Project</h1>
              <p className="text-white/60 text-lg">
                Choose a project to create a character in.
              </p>
            </div>

            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {currentProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setSelectedProjectId(project.id);
                      setShowProjectSelector(false);
                    }}
                    className="group relative bg-onyx border border-white/5 rounded-xl p-6 hover:border-indigo-500/30 hover:bg-zinc-900 transition-all text-left hover:shadow-lg hover:shadow-indigo-500/10"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <FolderIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate group-hover:text-violet-300 transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-xs text-white/50 mt-1">
                          {project.project_type || 'General'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <CaretLeftIcon className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-white/60">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <CaretRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Show My Characters page after project selection (when not in builder flow)
  if (selectedProjectId && !builderPhase && !showResumeOrNew) {
    const selectedProject = projects.find(p => p.id === selectedProjectId);

    return (
      <>
        <CharacterNameModal
          isOpen={showNameModal}
          onClose={() => setShowNameModal(false)}
          onSubmit={handleNameSubmit}
        />
        <div className="flex-1 w-full flex flex-col bg-carbon text-white overflow-hidden">
          <ProductionBar
            page="character"
            afterPageSelector={
              <span className="text-xs text-[#a0a0b0] font-medium">
                {completedCharacters.length} Character{completedCharacters.length !== 1 ? 's' : ''}
              </span>
            }
          />
          <div className="flex-1 w-full overflow-y-auto relative">


            {/* Delete Confirmation Modal - Centered within sidebar bounds */}
            {deleteConfirmCharacter && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
                style={{
                  left: 'var(--sidebar-left, 24px)',
                  right: 'var(--sidebar-right, 24px)'
                }}
              >
                <div className="bg-carbon border border-iron rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center">
                      <WarningCircle className="w-7 h-7 text-red-400" weight="fill" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Delete Character</h3>
                      <p className="text-sm text-gray-400">{deleteConfirmCharacter.name || 'Unnamed'}</p>
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm mb-6">
                    Are you sure you want to delete this character? This action cannot be undone and will permanently delete:
                  </p>

                  <ul className="text-sm text-gray-500 mb-6 space-y-2">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400/50" />
                      Character artwork and layers
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400/50" />
                      Voice samples and configurations
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400/50" />
                      Description and reference images
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400/50" />
                      Reference sheet and passport data
                    </li>
                  </ul>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setDeleteConfirmCharacter(null)}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-2.5 rounded-lg border border-iron text-neutral-200 hover:bg-graphite transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteCharacter}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash className="w-4 h-4" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="w-full mx-auto px-6 py-8">

              {/* My Characters Section */}
              {/* Character Grid - adaptive columns based on sidebar state */}
              <div
                id="characters-carousel"
                className="grid gap-4 pt-28"
                style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}
              >
                {/* All Characters (Draft + Finalized) - filtered by search */}
                {filteredCharacters.map((character) => {
                  const coreLayers = character.core_layers || { body: null, head: null };
                  // Show the most recent/complete layer (head > body)
                  // Prefer web_url (compressed) for faster thumbnail loading
                  // Also fallback to temp images from builder_session during creation
                  const viewportState = character.builder_session?.viewport_state;
                  const headshotUrl = coreLayers.head?.web_url ||
                    coreLayers.head?.rendered_url ||
                    coreLayers.body?.web_url ||
                    coreLayers.body?.rendered_url ||
                    viewportState?.headImageUrl ||
                    viewportState?.bodyImageUrl ||
                    null; // null means no layers yet
                  const createdDate = new Date(character.created_at);

                  // Calculate completion progress based on workflow:
                  // Steps light up when you're ON that step (reached it), not after completing it
                  // Step 1: Started character creation (has builder_session)
                  // Step 2: Past prompt builder (in dual-review/viewport or beyond)
                  // Step 3: Has generated images (head & body exist)
                  // Step 4: Has reference sheet/passport
                  const hasBody = !!coreLayers.body;
                  const hasHead = !!coreLayers.head;
                  const builderPhase = character.builder_session?.phase;

                  // Step 1: Started (has any builder session data, or is finalized, or has images)
                  const onStep1 = !!(character.builder_session || character.is_finalized || hasBody || hasHead);
                  // Step 2: Past prompt builder (in review, viewport, or has images)
                  const onStep2 = !!(character.builder_session?.review_data || character.builder_session?.viewport_state || builderPhase === 'dual-review' || builderPhase === 'dual-viewport' || hasBody || hasHead || character.is_finalized);
                  // Step 3: Has generated images
                  const onStep3 = (hasBody && hasHead) || character.is_finalized;
                  // Step 4: Has reference sheet or passport
                  const onStep4 = !!character.passport_id || !!coreLayers.body?.is_transposed || character.is_finalized;

                  // Voice data for this character
                  const voiceData = characterVoices[character.id];
                  const hasVoice = voiceData?.hasVoice ?? false;
                  const isPlaying = playingAudioId === character.id;

                  // Check if this card is being dragged
                  const isDragging = draggingCharacterId === character.id;

                  return (
                    <motion.div
                      key={character.id}
                      layout={!draggingCharacterId}  // Disable layout animation while any card is being dragged
                      layoutId={character.id}
                      initial={false}
                      transition={draggingCharacterId ? { duration: 0 } : { type: "spring", stiffness: 350, damping: 30 }}
                      onMouseDown={(e) => handleMouseDown(e, character.id)}
                      onClick={() => {
                        // Don't navigate if we just finished dragging
                        if (justDraggedRef.current || draggingCharacterId) return;
                        setShowCharacterProfile(character.id);
                        updateUrlState({
                          projectId: selectedProjectId || undefined,
                          view: 'profile',
                          characterId: character.id,
                        });
                      }}
                      className={`group relative overflow-hidden rounded-lg cursor-pointer transition-shadow ease-out ${isDragging
                        ? 'z-50 shadow-2xl shadow-slate-blue/40 scale-105 rotate-[-3deg]'
                        : draggingCharacterId
                          ? 'opacity-60 scale-[0.98]'
                          : 'hover:scale-[1.03] hover:shadow-2xl hover:shadow-black/50 hover:-translate-y-1 active:scale-[0.97] active:rotate-[1deg]'
                        }`}
                      style={{
                        ...(isDragging && dragPosition ? {
                          position: 'fixed' as const,
                          left: dragPosition.x - 140,  // Center on cursor (half of 280px width)
                          top: dragPosition.y - 180,   // Center on cursor (half of ~360px height)
                          width: '280px',              // Explicit width to prevent collapse
                          transform: 'scale(1.05) rotate(-3deg)',
                          zIndex: 1000,
                          pointerEvents: 'none' as const,  // Allow mouse events to pass through to trash bin
                        } : {}),
                      }}
                    >
                      {/* Card Frame - Premium Game Card Style */}
                      <div className="relative bg-gradient-to-b from-graphite via-zinc-900 to-zinc-950 p-[3px] rounded-xl shadow-xl group-hover:shadow-2xl group-hover:shadow-violet-500/20 transition-shadow duration-300">
                        {/* Gradient Border Overlay */}
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#5a5a68]/60 via-zinc-700/30 to-[#5a5a68]/60 opacity-100 group-hover:opacity-0 transition-opacity duration-300 pointer-events-none" />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-violet-500/40 via-transparent to-indigo-500/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                        {/* Inner Card Content */}
                        <div className="relative rounded-[9px] overflow-hidden bg-gradient-to-b from-carbon to-onyx">

                          {/* Top Banner with Name - Enhanced with glass effect */}
                          <div className="relative bg-gradient-to-r from-graphite/90 via-zinc-700/80 to-graphite/90 backdrop-blur-sm px-3 py-2.5 border-b border-white/[0.08]">
                            {/* Subtle top highlight */}
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                            {/* Voice Button - in header */}
                            <div
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => handlePlayVoice(e, character.id)}
                              draggable={false}
                              className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full flex items-center justify-center transition-all shadow-lg ring-1 ring-black/30 ${hasVoice
                                ? isPlaying
                                  ? 'bg-gradient-to-br from-violet-400 to-violet-600 cursor-pointer shadow-violet-500/50'
                                  : 'bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 cursor-pointer shadow-amber-500/30'
                                : 'bg-gradient-to-br from-rose-800/80 to-rose-900/80 cursor-default'
                                }`}
                              title={hasVoice ? (isPlaying ? 'Stop playback' : 'Play voice sample') : 'No voice configured'}
                            >
                              {hasVoice ? (
                                <SpeakerSimpleHigh
                                  className={`w-3.5 h-3.5 ${isPlaying ? 'text-white' : 'text-amber-950'}`}
                                  weight="fill"
                                />
                              ) : (
                                <SpeakerSimpleX className="w-3.5 h-3.5 text-rose-300/60" weight="fill" />
                              )}
                            </div>

                            {/* Character Name - with edit functionality */}
                            {editingCharacterId === character.id ? (
                              <div className="flex-1 flex items-center gap-1 pl-10 pr-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editingCharacterName}
                                  onChange={(e) => setEditingCharacterName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveCharacterName(character.id);
                                    if (e.key === 'Escape') handleCancelEditName();
                                  }}
                                  onBlur={() => handleSaveCharacterName(character.id)}
                                  autoFocus
                                  className="flex-1 bg-zinc-900 text-gray-100 text-sm px-2 py-0.5 rounded border border-white/20 focus:outline-none focus:border-indigo-400"
                                />
                              </div>
                            ) : (
                              <>
                                <h3 className="flex-1 font-semibold text-gray-100 text-sm truncate text-center tracking-wide drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] px-8">
                                  {character.name || 'Unnamed'}
                                </h3>
                                {/* Edit Button */}
                                <button
                                  onClick={(e) => handleStartEditName(e, character)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10 transition-colors"
                                  title="Edit name"
                                >
                                  <Pencil className="w-3 h-3 text-zinc-400 hover:text-white" weight="regular" />
                                </button>
                              </>
                            )}
                          </div>

                          {/* Character Image */}
                          <div className="aspect-[3/4] relative bg-zinc-950 overflow-hidden isolate">
                            {headshotUrl ? (
                              <img
                                src={headshotUrl}
                                alt={character.name || 'Character'}
                                draggable={false}
                                className="absolute inset-[-1px] w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover object-top scale-100 group-hover:scale-[1.08] transition-transform duration-700 ease-out will-change-transform"
                              />
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-carbon via-neutral-900 to-zinc-950">
                                <UserIcon className="w-16 h-16 text-graphite opacity-60" />
                              </div>
                            )}


                          </div>

                          {/* Bottom Progress Bar - Enhanced */}
                          <div className="bg-gradient-to-b from-zinc-900 to-onyx px-3 py-2 border-t border-white/[0.05]">
                            <div className="flex gap-1.5">
                              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${onStep1 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-graphite'}`} title="Prompt" />
                              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${onStep2 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-graphite'}`} title="Generate" />
                              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${onStep3 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-graphite'}`} title="Reference" />
                              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${onStep4 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-graphite'}`} title="Finalized" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Top Search/Create Pill Island */}
            <div
              className="fixed top-20 z-[1100] transition-all duration-300 flex items-center"
              style={{
                left: `calc(50% + ${(() => {
                  const NAV_WIDTH = 240;
                  const PANEL_WIDTH = 640;
                  const leftWidth = sidebarState.navPosition === 'left'
                    ? (sidebarState.isSidebarOpen ? NAV_WIDTH : 0)
                    : (sidebarState.isRightPanelOpen ? PANEL_WIDTH : 0);
                  const rightWidth = sidebarState.navPosition === 'left'
                    ? (sidebarState.isRightPanelOpen ? PANEL_WIDTH : 0)
                    : (sidebarState.isSidebarOpen ? NAV_WIDTH : 0);
                  return (leftWidth - rightWidth) / 2;
                })()}px)`,
                transform: 'translateX(-50%)'
              }}
            >
              <div className="flex items-center bg-zinc-900 rounded-full border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
                {/* Search Icon */}
                <div className="pl-5 pr-2 text-white/40">
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </div>

                {/* Search/Name Input */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && searchQuery.trim() && user) {
                      const name = searchQuery.trim();

                      // CRITICAL: Clear old prompt builder state before creating new character
                      setPromptBuilderData(null);
                      setDualReviewData(null);
                      setSelectedArchetype(null);
                      clearBuilderPhaseData();

                      // Create draft character immediately so it appears in the list
                      const newCharacter: Character2DLayered = {
                        id: pendingCharacterId,
                        user_id: user.id,
                        project_id: selectedProjectId,
                        name: name,
                        archetype: undefined as any, // Will be set in prompt builder
                        form: 'humanoid',
                        style_preset: 'photorealistic',
                        core_layers: {} as any,
                        current_step: 1,
                        completed_steps: [],
                        is_finalized: false,
                        status: 'draft',
                        character_type: '2d_layered',
                        builder_session: {
                          phase: 'prompt-builder' as const,
                          model: 'official',
                        },
                        created_at: Date.now(),
                        updated_at: Date.now(),
                      };
                      try {
                        await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
                        setExistingCharacter(newCharacter);
                        console.log('📝 Created draft character immediately:', pendingCharacterId);
                      } catch (error) {
                        console.error('Failed to create draft character:', error);
                      }
                      // Create new character with the typed name
                      setCharacterName(name);
                      setSearchQuery('');
                      setShowNameModal(false);
                      // Start new character creation
                      setBuilderPhase('prompt-builder');
                      setSelectedModel('official');
                      setIsCreatingNew(true);
                      localStorage.setItem(KEYS.MODEL, 'official');
                      // Trigger refetch to show new character in list
                      setRefetchTrigger(prev => prev + 1);
                      // Generate new pending ID for next character
                      const nextId = generateId();
                      setPendingCharacterId(nextId);
                      localStorage.setItem('ezart_pending_character_id', nextId);
                    }
                  }}
                  placeholder="Search or type a name..."
                  className="w-64 py-4 bg-transparent text-white placeholder:text-white/30 focus:outline-none text-sm"
                />

                {/* Create Button - requires at least 1 character */}
                <button
                  onClick={async () => {
                    const name = searchQuery.trim();
                    if (!name || !user) return;

                    // CRITICAL: Clear old prompt builder state before creating new character
                    setPromptBuilderData(null);
                    setDualReviewData(null);
                    setSelectedArchetype(null);
                    clearBuilderPhaseData();

                    // Create draft character immediately so it appears in the list
                    const newCharacter: Character2DLayered = {
                      id: pendingCharacterId,
                      user_id: user.id,
                      project_id: selectedProjectId,
                      name: name,
                      archetype: undefined as any, // Will be set in prompt builder
                      form: 'humanoid',
                      style_preset: 'photorealistic',
                      core_layers: {} as any,
                      current_step: 1,
                      completed_steps: [],
                      is_finalized: false,
                      status: 'draft',
                      character_type: '2d_layered',
                      builder_session: {
                        phase: 'prompt-builder' as const,
                        model: 'official',
                      },
                      created_at: Date.now(),
                      updated_at: Date.now(),
                    };
                    try {
                      await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
                      setExistingCharacter(newCharacter);
                      console.log('📝 Created draft character immediately:', pendingCharacterId);
                    } catch (error) {
                      console.error('Failed to create draft character:', error);
                    }
                    setCharacterName(name);
                    setSearchQuery('');
                    setShowNameModal(false);
                    // Start new character creation
                    setBuilderPhase('prompt-builder');
                    setSelectedModel('official');
                    setIsCreatingNew(true);
                    localStorage.setItem(KEYS.MODEL, 'official');
                    // Trigger refetch to show new character in list
                    setRefetchTrigger(prev => prev + 1);
                    // Generate new pending ID for next character
                    const nextId = generateId();
                    setPendingCharacterId(nextId);
                    localStorage.setItem('ezart_pending_character_id', nextId);
                  }}
                  disabled={!searchQuery.trim() || !permissions.canEdit}
                  className={`h-full px-5 py-4 transition-colors flex items-center gap-2 text-sm font-medium ${
                    searchQuery.trim() && permissions.canEdit
                      ? 'bg-slate-blue hover:bg-[#8886F9] text-white'
                      : 'bg-neutral-700 text-white/30 cursor-not-allowed'
                  }`}
                  title={!permissions.canEdit ? 'You don\'t have permission to create characters' : undefined}
                >
                  <PlusIcon className="w-5 h-5" />
                  Create
                </button>
              </div>

              {/* Trash Bin - appears when dragging a character (only for editors) */}
              <div
                ref={trashBinRef}
                className={`ml-3 transition-all duration-300 ${draggingCharacterId && permissions.canEdit ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none w-0 ml-0'}`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${isOverTrash
                    ? 'bg-red-500/30 border-2 border-red-500 scale-110 shadow-lg shadow-red-500/30'
                    : 'bg-zinc-900 border border-white/10 shadow-lg'
                  }`}
                >
                  {isOverTrash ? (
                    <Trash className="w-5 h-5 text-red-400" weight="fill" />
                  ) : (
                    <TrashSimple className="w-5 h-5 text-gray-500" weight="regular" />
                  )}
                </div>
              </div>

              {/* Search results hint - below the search bar */}
              {searchQuery.trim() && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 px-3 py-1.5 bg-zinc-900/90 rounded-lg border border-white/10 text-xs text-white/50">
                  {filteredCharacters.length === 0
                    ? `No characters found • Press Enter to create "${searchQuery.trim()}"`
                    : `${filteredCharacters.length} character${filteredCharacters.length !== 1 ? 's' : ''} found`
                  }
                </div>
              )}
            </div>

          </div>
        </div>

      </>
    );
  }

  // NOTE: Archetype and model selection screens have been removed.
  // Medium selection is now integrated into PromptBuilder.
  // Model defaults to 'official' - can be changed in settings later.

  // ============================================
  // NEW BUILDER PHASE SYSTEM
  // ============================================

  // Initialize builder phase when project is selected
  // Model defaults to 'official' if not set - all characters use new flow
  // NOTE: We do NOT auto-restore builder phase on refresh anymore.
  // On refresh, user lands on character selection page and can click "Resume" on their in-progress character.
  // This provides a cleaner UX and avoids issues with partially loaded state.

  // Phase 1: Prompt Builder (archetype/medium selection is now integrated here)
  if (builderPhase === 'prompt-builder' && selectedProjectId) {
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-onyx">
        <PromptBuilder
          key={existingCharacter?.id || pendingCharacterId}
          archetype={selectedArchetype || undefined}
          onConfirm={async (description, selectedTags, analysis, selectedStyle, archetype, referenceImages) => {
            // Update archetype from PromptBuilder selection
            if (archetype) {
              setSelectedArchetype(archetype);
              // Also derive form from censorship type
              const isCreature = analysis?.censorshipType?.startsWith('creature');
              const form = isCreature ? 'creature' : 'humanoid';
              setSelectedForm(form);
            }

            setPromptBuilderData({ description, selectedTags, analysis, selectedStyle, referenceImages });
            setBuilderPhase('dual-review');

            // Save builder session to character for resume capability
            // CRITICAL: Create character if it doesn't exist (new characters)
            if (user && archetype) {
              try {
                const effectiveModel = selectedModel || 'official';
                const effectiveForm = analysis?.censorshipType?.startsWith('creature') ? 'creature' as const : 'humanoid' as const;

                if (existingCharacter) {
                  // UPDATE existing character
                  const updatedCharacter = {
                    ...existingCharacter,
                    archetype: archetype,
                    form: effectiveForm,
                    style_preset: selectedStyle?.id || getStylePresetForArchetype(archetype),
                    builder_session: {
                      phase: 'dual-review' as const,
                      model: effectiveModel,
                      prompt_data: { description, selectedTags, analysis, selectedStyle, referenceImages },
                    },
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId });
                  setExistingCharacter(updatedCharacter);
                } else {
                  // CREATE new draft character
                  const newCharacter: Character2DLayered = {
                    id: pendingCharacterId,
                    user_id: user.id,
                    project_id: selectedProjectId,
                    name: characterName || 'New Character',
                    archetype: archetype,
                    form: effectiveForm,
                    style_preset: selectedStyle?.id || getStylePresetForArchetype(archetype),
                    core_layers: {} as any, // Will be populated when images are generated
                    current_step: 1,
                    completed_steps: [],
                    is_finalized: false,
                    status: 'draft',
                    character_type: '2d_layered',
                    builder_session: {
                      phase: 'dual-review' as const,
                      model: effectiveModel,
                      prompt_data: { description, selectedTags, analysis, selectedStyle, referenceImages },
                    },
                    created_at: Date.now(),
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
                  setExistingCharacter(newCharacter);
                  console.log('📝 Created draft character for state persistence:', pendingCharacterId);
                }
              } catch (error) {
                console.error('Failed to save builder session:', error);
              }
            }
          }}
          onBack={() => {
            // Go back to My Characters page
            // Set backed out flag to prevent auto-resume from re-triggering
            hasBackedOut.current = true;
            hasAttemptedResume.current = null;
            // Clear all workflow state
            setBuilderPhase(null);
            setSelectedModel(null);
            setSelectedArchetype(null);
            setShowResumeOrNew(false);
            setExistingCharacter(null);
            setIsCreatingNew(false);
            setPromptBuilderData(null);
            // Clear localStorage
            localStorage.removeItem(KEYS.MODEL);
            localStorage.removeItem(KEYS.ARCHETYPE);
            localStorage.removeItem(KEYS.SHOW_RESUME_OR_NEW);
            localStorage.removeItem(KEYS.ACTIVE_CHARACTER_ID);
            clearBuilderPhaseData();
            // Trigger refetch to show any saved characters
            setRefetchTrigger(prev => prev + 1);
            // Force navigation to My Characters page
            navigate(`/character?project=${selectedProjectId}`, { replace: true });
          }}
          initialDescription={promptBuilderData?.description || existingCharacter?.builder_session?.prompt_data?.description}
          initialTags={promptBuilderData?.selectedTags || existingCharacter?.builder_session?.prompt_data?.selectedTags}
          initialStyle={promptBuilderData?.selectedStyle || existingCharacter?.builder_session?.prompt_data?.selectedStyle}
          initialReferenceImages={promptBuilderData?.referenceImages || existingCharacter?.builder_session?.prompt_data?.referenceImages}
          projectId={selectedProjectId}
          characterId={existingCharacter?.id || pendingCharacterId}
        />
      </div>
    );
  }

  // Phase 2: Dual Prompt Review
  if (builderPhase === 'dual-review' && promptBuilderData) {
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-onyx">
        <DualPromptReview
          description={promptBuilderData.description}
          selectedTags={promptBuilderData.selectedTags}
          analysis={promptBuilderData.analysis!}
          form={selectedForm}
          archetype={selectedArchetype || existingCharacter?.archetype || 'film'}
          selectedStyle={promptBuilderData.selectedStyle}
          referenceImages={promptBuilderData.referenceImages}
          // Pass saved prompts to skip regeneration when going back (if description unchanged)
          initialBodyPrompt={dualReviewData?.bodyPrompt || existingCharacter?.builder_session?.review_data?.bodyPrompt}
          initialHeadPrompt={dualReviewData?.headPrompt || existingCharacter?.builder_session?.review_data?.headPrompt}
          initialSharedAttributes={dualReviewData?.sharedAttributes || existingCharacter?.builder_session?.review_data?.sharedAttributes}
          savedSourceDescription={dualReviewData?.sourceDescription || existingCharacter?.builder_session?.review_data?.sourceDescription}
          initialClassifiedImages={dualReviewData?.classifiedImages || existingCharacter?.builder_session?.review_data?.classifiedImages}
          onPromptsGenerated={async (splitPrompts, sourceDescription) => {
            // Save prompts immediately when generated (before user clicks Generate)
            // This enables back/forward persistence without re-analyzing
            console.log('📋 Saving generated prompts for persistence');
            const reviewData = {
              bodyPrompt: splitPrompts.bodyPrompt,
              headPrompt: splitPrompts.headPrompt,
              sharedAttributes: splitPrompts.sharedAttributes,
              selectedStyle: promptBuilderData.selectedStyle,
              referenceImages: promptBuilderData.referenceImages,
              sourceDescription,
              classifiedImages: splitPrompts.classifiedImages,  // Save AI image classifications
            };
            setDualReviewData(reviewData);

            // Also persist to Supabase for cross-session persistence
            if (user && existingCharacter) {
              try {
                const updatedCharacter = {
                  ...existingCharacter,
                  builder_session: {
                    ...existingCharacter.builder_session,
                    phase: 'dual-review' as const,
                    prompt_data: promptBuilderData,
                    review_data: reviewData,
                  },
                  updated_at: Date.now(),
                };
                await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId });
                setExistingCharacter(updatedCharacter);
                console.log('📋 Saved review_data to Supabase for persistence');
              } catch (error) {
                console.error('Failed to save review_data to Supabase:', error);
              }
            }
          }}
          onConfirm={async (bodyPrompt, headPrompt, sharedAttributes, generationModel, referenceImages) => {
            setDualReviewData({ bodyPrompt, headPrompt, sharedAttributes, selectedStyle: promptBuilderData.selectedStyle, sourceDescription: promptBuilderData.description, referenceImages, classifiedImages: dualReviewData?.classifiedImages });
            // Update selectedModel based on DualPromptReview's model selection
            const effectiveModel: 'official' | 'testing' = 'official';
            setSelectedModel(effectiveModel);
            // Increment key to force fresh DualViewportEditor mount (clears stale state)
            setViewportKey(prev => prev + 1);

            // Save builder session to character for resume capability
            // CRITICAL: Create character if it doesn't exist (fallback for edge cases)
            if (user) {
              try {
                const reviewData = { bodyPrompt, headPrompt, sharedAttributes, selectedStyle: promptBuilderData.selectedStyle, sourceDescription: promptBuilderData.description, referenceImages, classifiedImages: dualReviewData?.classifiedImages };

                if (existingCharacter) {
                  // UPDATE existing character
                  const updatedCharacter = {
                    ...existingCharacter,
                    builder_session: {
                      phase: 'feature-editor' as const,
                      feature_editor: existingCharacter?.builder_session?.review_data?.headPrompt === headPrompt && existingCharacter?.builder_session?.review_data?.bodyPrompt === bodyPrompt ? existingCharacter.builder_session.feature_editor : undefined,
                      model: effectiveModel,
                      prompt_data: promptBuilderData,
                      review_data: reviewData,
                    },
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId });
                  setExistingCharacter(updatedCharacter);
                } else {
                  // CREATE new draft character (fallback if Phase 1 didn't create one)
                  const effectiveArchetype = selectedArchetype || 'film';
                  const newCharacter: Character2DLayered = {
                    id: pendingCharacterId,
                    user_id: user.id,
                    project_id: selectedProjectId,
                    name: characterName || 'New Character',
                    archetype: effectiveArchetype,
                    form: selectedForm,
                    style_preset: promptBuilderData?.selectedStyle?.id || getStylePresetForArchetype(effectiveArchetype),
                    core_layers: {} as any,
                    current_step: 1,
                    completed_steps: [],
                    is_finalized: false,
                    status: 'draft',
                    character_type: '2d_layered',
                    builder_session: {
                      phase: 'feature-editor' as const,
                      model: effectiveModel,
                      prompt_data: promptBuilderData,
                      review_data: reviewData,
                    },
                    created_at: Date.now(),
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
                  setExistingCharacter(newCharacter);
                  console.log('📝 Created draft character for state persistence (Phase 2 fallback):', pendingCharacterId);
                }
              } catch (error) {
                console.error('Failed to save builder session:', error);
                showToast('Could not save the character draft. Please try again.', 'error');
                return;
              }
            }
            setBuilderPhase('feature-editor');
          }}
          onBack={() => {
            // Keep dualReviewData intact - prompts will be restored from cache if description unchanged
            // If user changes description in PromptBuilder, the cache check (savedSourceDescription !== description)
            // will trigger regeneration automatically
            setBuilderPhase('prompt-builder');
          }}
          onReturnToCharacters={() => {
            // Clear all builder state and return to My Characters
            clearBuilderPhaseData();
            setRefetchTrigger(prev => prev + 1);
            navigate(`/character?project=${selectedProjectId}`, { replace: true });
          }}
        />
      </div>
    );
  }

  // Head-first feature editor; legacy dual viewport remains resumable.
  const effectiveArchetype = selectedArchetype || existingCharacter?.archetype || 'film';

  const completeCharacterFromImages = async (result: DualViewportResult) => {
    if (!user) throw new Error("Sign in to save your character.");
    setDualViewportResult(result);

    // Create the character with the generated images
    // Use existing character ID if resuming, otherwise use pending ID
    const characterId = existingCharacter?.id || pendingCharacterId;

    try {
      // 1. SKIP Finalization here - we defer it to ReferenceSheetPreview
      // This prevents temp images from being deleted if the user goes "Back" from preview
      // We use the temp URLs directly for now

      const finalUrls = {
        bodyUrl: result.bodyImageUrl,
        headUrl: result.headImageUrl,
        transposedBodyUrl: result.transposedBodyUrl,
        nsfwUrl: result.nsfwImageUrl,
        webBodyUrl: undefined, // No web versions yet
        webHeadUrl: undefined,
        webTransposedUrl: undefined
      };

      // 2. Create/Update character with TEMP URLs
      const newCharacter: Character2DLayered = {
        id: characterId,
        user_id: user.id,
        project_id: selectedProjectId,
        name: characterName || existingCharacter?.name || 'New Character',
        archetype: effectiveArchetype,
        form: selectedForm,
        style_preset: promptBuilderData?.selectedStyle?.id || getStylePresetForArchetype(effectiveArchetype),
        core_layers: {
          body: {
            id: generateId(),
            type: 'body',
            step: 1,
            source: 'generated',
            source_url: result.bodyImageUrl,
            custom_body_url: result.customBodyImageUrl,
            rendered_url: result.transposedBodyUrl,
            web_url: result.transposedBodyUrl, // Temp URL for now
            edits: [],
            prompt_used: result.bodyPrompt,
            user_prompt: promptBuilderData?.description || '',
            description: 'Character body with transposed head',
            gender: result.sharedAttributes.gender,
            ethnicity: result.sharedAttributes.ethnicity,
            skin_tone: result.sharedAttributes.skinTone,
            created_at: Date.now(),
            modified_at: Date.now(),
          },
          head: {
            id: generateId(),
            type: 'head',
            step: 2,
            source: 'generated',
            source_url: result.headImageUrl,
            rendered_url: result.headImageUrl,
            web_url: result.headImageUrl, // Temp URL for now
            edits: [],
            prompt_used: result.headPrompt,
            user_prompt: promptBuilderData?.description || '',
            description: 'Character head',
            gender: result.sharedAttributes.gender,
            created_at: Date.now(),
            modified_at: Date.now(),
          },
        },
        nsfw_body_url: result.nsfwImageUrl,
        current_step: 2,
        completed_steps: [1, 2],
        is_finalized: false,
        character_type: '2d_layered',
        builder_session: existingCharacter?.builder_session,
        created_at: existingCharacter?.created_at || Date.now(),
        updated_at: Date.now(),
      };

      // 3. Save character to database (still draft/temp state)
      showToast('Generating reference sheet...', 'success');
      await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });

      // 4. Generate passport/reference sheet (with loading overlay)
      setIsGeneratingRefSheet(true);
      let passportResult;
      try {
        passportResult = await generateCharacterPassport(newCharacter);
      } finally {
        setIsGeneratingRefSheet(false);
      }

      // 5. Store data and go to reference sheet preview phase
      setReferenceSheetUrl(passportResult.referenceSheetUrl);
      setReferenceSheetUnclothedUrl(null);
      setReferenceSheetVersion('original');
      setPendingCharacterData(newCharacter);
      setPendingPassportData({
        textData: passportResult.textData,
        usage_prompt: passportResult.usage_prompt,
      });

      // 5b. Auto-save reference sheet data to builder_session for resume capability
      // This ensures the reference sheet survives page refresh before final confirmation
      const characterWithRefSheet = {
        ...newCharacter,
        builder_session: {
          ...newCharacter.builder_session,
          phase: 'reference-sheet-preview' as const,
          viewport_state: existingCharacter?.builder_session?.viewport_state,
          model: selectedModel || 'official',
          reference_sheet_url: passportResult.referenceSheetUrl,
          reference_sheet_version: 'original' as const,
          passport_data: {
            textData: passportResult.textData,
            usage_prompt: passportResult.usage_prompt,
          },
        },
        updated_at: Date.now(),
      };
      await saveCharacter2D({ ...characterWithRefSheet, project_id: selectedProjectId });
      setPendingCharacterData(characterWithRefSheet);
      console.log('📋 Auto-saved reference sheet to builder_session for resume capability');

      setBuilderPhase('reference-sheet-preview');
      updateUrlState({
        projectId: selectedProjectId || undefined,
        phase: 'reference-sheet-preview',
        characterId: newCharacter.id,
      });

    } catch (error) {
      console.error('Failed to complete character creation:', error);
      showToast('Failed to generate reference sheet. Please try again.', 'error');
      setIsGeneratingRefSheet(false);
      throw error;
    }
  };


  if (builderPhase === 'feature-editor' && dualReviewData && user && !isLoadingCharacterData) {
    const saveFeatureSession = async (featureSession: FeatureEditorSession) => {
      const draft: Character2DLayered = {
        ...(existingCharacter || {
          id: pendingCharacterId, user_id: user.id, project_id: selectedProjectId,
          name: characterName || 'New Character', archetype: effectiveArchetype, form: selectedForm,
          style_preset: promptBuilderData?.selectedStyle?.id || getStylePresetForArchetype(effectiveArchetype),
          core_layers: {} as Character2DLayered['core_layers'], current_step: 1,
          completed_steps: [], is_finalized: false, status: 'draft', character_type: '2d_layered', created_at: Date.now(),
        }),
        builder_session: {
          ...existingCharacter?.builder_session, phase: 'feature-editor', model: 'official',
          prompt_data: promptBuilderData || existingCharacter?.builder_session?.prompt_data,
          review_data: dualReviewData, feature_editor: featureSession,
        }, updated_at: Date.now(),
      };
      await saveCharacter2D(draft);
      setExistingCharacter(draft);
    };
    return <CharacterFeatureEditor
      key={`features-${existingCharacter?.id || pendingCharacterId}-${viewportKey}`}
      characterId={existingCharacter?.id || pendingCharacterId} projectId={selectedProjectId} userId={user.id}
      characterName={characterName || existingCharacter?.name || 'Your character'}
      bodyPrompt={dualReviewData.bodyPrompt} headPrompt={dualReviewData.headPrompt}
      description={promptBuilderData?.description || dualReviewData.sourceDescription || ''}
      sharedAttributes={dualReviewData.sharedAttributes} archetype={effectiveArchetype} form={selectedForm}
      selectedStyle={dualReviewData.selectedStyle} referenceImages={dualReviewData.referenceImages}
      classifiedImages={dualReviewData.classifiedImages}
      initialSession={existingCharacter?.builder_session?.feature_editor}
      onSessionChange={saveFeatureSession} onConfirm={completeCharacterFromImages}
      onBack={() => setBuilderPhase('dual-review')}
    />;
  }

  // Show loading state when resuming to dual-viewport but data isn't ready yet
  if ((builderPhase === 'dual-viewport' || builderPhase === 'feature-editor') && selectedModel && user && (!dualReviewData || isLoadingCharacterData)) {
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-onyx items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-slate-blue/30 border-t-slate-blue animate-spin" />
          <p className="text-sm text-gray-500">Loading character session...</p>
        </div>
      </div>
    );
  }

  if (builderPhase === 'dual-viewport' && dualReviewData && selectedModel && user) {
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-onyx relative">
        {/* Reference Sheet Generation Overlay */}
        {isGeneratingRefSheet && (
          <div className="absolute inset-0 z-50 bg-onyx/95 flex flex-col items-center justify-center">
            <div className="flex flex-col items-center gap-6 max-w-md text-center">
              <div className="w-16 h-16 rounded-full border-4 border-slate-blue/30 border-t-slate-blue animate-spin" />
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Generating Reference Sheet</h3>
                <p className="text-sm text-gray-500">
                  Creating your character's reference sheet with multiple poses and angles. This may take a moment...
                </p>
              </div>
            </div>
          </div>
        )}
        <DualViewportEditor
          key={`viewport-${viewportKey}`}
          bodyPrompt={dualReviewData.bodyPrompt}
          headPrompt={dualReviewData.headPrompt}
          sharedAttributes={dualReviewData.sharedAttributes}
          stylePreset={getStylePresetForArchetype(effectiveArchetype)}
          archetype={effectiveArchetype}
          selectedStyle={dualReviewData.selectedStyle}
          form={selectedForm}
          generationModel={selectedModel === 'official' ? 'openai/gpt-image-2.5-sunburst' : 'prunaai/z-image-turbo'}
          quality="HQ"
          userId={user.id}
          projectId={selectedProjectId}
          characterId={pendingCharacterId}
          referenceImages={dualReviewData.referenceImages}
          // Pass viewport state from Supabase builder_session for resume
          initialViewportState={existingCharacter?.builder_session?.viewport_state}
          initialCustomBodyUrl={existingCharacter?.core_layers.body?.custom_body_url}
          onViewportStateChange={async (viewportState: ViewportState) => {
            // Save viewport state to Supabase builder_session
            // CRITICAL: Create character if it doesn't exist (fallback for edge cases)
            if (selectedModel) {
              try {
                if (existingCharacter) {
                  // UPDATE existing character
                  const updatedCharacter = {
                    ...existingCharacter,
                    builder_session: {
                      ...existingCharacter.builder_session,
                      phase: 'dual-viewport' as const,
                      model: selectedModel,
                      viewport_state: viewportState,
                    },
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId });
                  // Update local state without triggering re-render loop
                  setExistingCharacter(prev => prev ? { ...prev, builder_session: updatedCharacter.builder_session } : null);
                } else {
                  // CREATE new draft character (fallback if Phase 1/2 didn't create one)
                  const newCharacter: Character2DLayered = {
                    id: pendingCharacterId,
                    user_id: user.id,
                    project_id: selectedProjectId,
                    name: characterName || 'New Character',
                    archetype: effectiveArchetype,
                    form: selectedForm,
                    style_preset: promptBuilderData?.selectedStyle?.id || getStylePresetForArchetype(effectiveArchetype),
                    core_layers: {} as any,
                    current_step: 1,
                    completed_steps: [],
                    is_finalized: false,
                    status: 'draft',
                    character_type: '2d_layered',
                    builder_session: {
                      phase: 'dual-viewport' as const,
                      model: selectedModel,
                      prompt_data: promptBuilderData || undefined,
                      review_data: dualReviewData || undefined,
                      viewport_state: viewportState,
                    },
                    created_at: Date.now(),
                    updated_at: Date.now(),
                  };
                  await saveCharacter2D({ ...newCharacter, project_id: selectedProjectId });
                  setExistingCharacter(newCharacter);
                  console.log('📝 Created draft character for state persistence (Phase 3 fallback):', pendingCharacterId);
                }
              } catch (error) {
                console.error('Failed to save viewport state:', error);
                throw error;
              }
            }
          }}
          onConfirm={async result => {
            try { await completeCharacterFromImages(result); } catch { /* The completion handler shows the error; legacy drafts stay open. */ }
          }}
          onBack={async () => {
            // Clear all viewport and review data - go back to prompt builder with clean slate
            setDualViewportResult(null);
            setDualReviewData(null);
            setPromptBuilderData(null);
            setBuilderPhase('prompt-builder');

            // Clear builder session from Supabase so user starts fresh
            if (existingCharacter && selectedModel) {
              try {
                const updatedCharacter = {
                  ...existingCharacter,
                  builder_session: {
                    phase: 'prompt-builder' as const,
                    model: selectedModel,
                    // Clear all saved data - user starts fresh
                    prompt_data: undefined,
                    review_data: undefined,
                    viewport_state: undefined,
                  },
                  updated_at: Date.now(),
                };
                await saveCharacter2D({ ...updatedCharacter, project_id: selectedProjectId });
                setExistingCharacter(updatedCharacter);
                console.log('📝 Cleared all builder data on back to prompt builder');
              } catch (error) {
                console.error('Failed to clear builder state:', error);
              }
            }

            // Also clear localStorage
            localStorage.removeItem('ezart_prompt_builder_local');
          }}
          onReturnToCharacters={() => {
            // Return to character selection - clear all builder state
            // NOTE: We keep builder_session in database so user can resume later via "Resume" button
            setBuilderPhase(null);
            setPromptBuilderData(null);
            setDualReviewData(null);
            setDualViewportResult(null);
            setSelectedArchetype(null);
            setSelectedModel(null);
            setCharacterName('');
            setExistingCharacter(null);
            setShowResumeOrNew(false); // Clear resume/new flag to show character list
            setIsCreatingNew(false); // Clear creating new flag
            // Clear localStorage keys to prevent stale state on refresh
            localStorage.removeItem(KEYS.SHOW_RESUME_OR_NEW);
            localStorage.removeItem(KEYS.ARCHETYPE);
            localStorage.removeItem(KEYS.MODEL);
            localStorage.removeItem(KEYS.ACTIVE_CHARACTER_ID);
            clearBuilderPhaseData();
            // Generate new pending ID for next character
            const newId = generateId();
            setPendingCharacterId(newId);
            localStorage.setItem('ezart_pending_character_id', newId);
            // Trigger refetch to show newly saved character
            setRefetchTrigger(prev => prev + 1);
            // Update URL
            updateUrlState({
              projectId: selectedProjectId || undefined,
            });
          }}
        />
      </div>
    );
  }

  // Phase 4: Reference Sheet Preview
  if (builderPhase === 'reference-sheet-preview' && referenceSheetUrl && pendingCharacterData && user) {
    const persistSheetPreview = async (
      originalUrl: string,
      unclothedUrl: string | null,
      version: 'original' | 'unclothed',
      passportData = pendingPassportData
    ) => {
      const updatedCharacter: Character2DLayered = {
        ...pendingCharacterData,
        project_id: selectedProjectId || pendingCharacterData.project_id,
        builder_session: {
          ...existingCharacter?.builder_session,
          ...pendingCharacterData.builder_session,
          phase: 'reference-sheet-preview',
          model: selectedModel || 'official',
          reference_sheet_url: originalUrl,
          reference_sheet_unclothed_url: unclothedUrl || undefined,
          reference_sheet_version: version,
          passport_data: passportData || undefined,
        },
        updated_at: Date.now(),
      };
      await saveCharacter2D(updatedCharacter);
      setPendingCharacterData(updatedCharacter);
    };
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-onyx">
        <ReferenceSheetPreview
          referenceSheetUrl={referenceSheetUrl}
          isSaving={isSavingReferenceSheet}
          characterName={characterName || pendingCharacterData.name || 'New Character'}
          isRegenerating={isGeneratingRefSheet}
          onRegenerate={async () => {
            // Regenerate the reference sheet
            setIsGeneratingRefSheet(true);
            try {
              const passportResult = await generateCharacterPassport(pendingCharacterData);
              const passportData = {
                textData: passportResult.textData,
                usage_prompt: passportResult.usage_prompt,
              };
              await persistSheetPreview(passportResult.referenceSheetUrl, null, 'original', passportData);
              setReferenceSheetUrl(passportResult.referenceSheetUrl);
              setReferenceSheetUnclothedUrl(null);
              setReferenceSheetVersion('original');
              setPendingPassportData(passportData);
              showToast('Reference sheet regenerated!', 'success');
            } catch (error) {
              console.error('Failed to regenerate reference sheet:', error);
              showToast('Failed to regenerate reference sheet. Please try again.', 'error');
            } finally {
              setIsGeneratingRefSheet(false);
            }
          }}
          onConfirm={async () => {
            if (!pendingPassportData || isSavingReferenceSheet) return;
            setIsSavingReferenceSheet(true);

            try {
              // 1. Finalize images (move from temp to final)
              // We use dualViewportResult if available (has blobs), otherwise fallback to pendingCharacterData urls
              // Note: dualViewportResult is preferred as it has the blobs for optimization
              showToast('Finalizing images...', 'success');

              // Helper to get URL from pending data if dualViewportResult is missing
              const getTempUrl = (type: 'body' | 'head' | 'transposed' | 'nsfw') => {
                if (dualViewportResult) {
                  if (type === 'body') return dualViewportResult.bodyImageUrl;
                  if (type === 'head') return dualViewportResult.headImageUrl;
                  if (type === 'transposed') return dualViewportResult.transposedBodyUrl;
                  if (type === 'nsfw') return dualViewportResult.nsfwImageUrl;
                }
                // Fallback to what we saved in pendingCharacterData
                if (type === 'body') return pendingCharacterData.core_layers.body?.source_url;
                if (type === 'head') return pendingCharacterData.core_layers.head?.source_url;
                if (type === 'transposed') return pendingCharacterData.core_layers.body?.rendered_url;
                if (type === 'nsfw') return pendingCharacterData.nsfw_body_url;
                return undefined;
              };

              const finalUrls = await finalizeCharacterImages(
                {
                  bodyUrl: getTempUrl('body'),
                  headUrl: getTempUrl('head'),
                  transposedBodyUrl: getTempUrl('transposed'),
                  nsfwUrl: getTempUrl('nsfw'),
                  referenceSheetUrl: referenceSheetUrl,
                  unclothedReferenceSheetUrl: referenceSheetUnclothedUrl || undefined,
                },
                user.id,
                selectedProjectId,
                pendingCharacterData.id,
                {
                  bodyBlob: dualViewportResult?.bodyBlob,
                  headBlob: dualViewportResult?.headBlob,
                  transposedBodyBlob: dualViewportResult?.transposedBodyBlob,
                  nsfwBlob: dualViewportResult?.nsfwBlob,
                }
              );

              // 2. Update character data with final URLs
              const finalizedCharacterData: Character2DLayered = {
                ...pendingCharacterData,
                core_layers: {
                  ...pendingCharacterData.core_layers,
                  body: pendingCharacterData.core_layers.body ? {
                    ...pendingCharacterData.core_layers.body,
                    source_url: finalUrls.bodyUrl || pendingCharacterData.core_layers.body.source_url,
                    rendered_url: finalUrls.transposedBodyUrl || pendingCharacterData.core_layers.body.rendered_url,
                    web_url: finalUrls.webTransposedUrl || finalUrls.transposedBodyUrl || pendingCharacterData.core_layers.body.web_url,
                  } : pendingCharacterData.core_layers.body,
                  head: pendingCharacterData.core_layers.head ? {
                    ...pendingCharacterData.core_layers.head,
                    source_url: finalUrls.headUrl || pendingCharacterData.core_layers.head.source_url,
                    rendered_url: finalUrls.headUrl || pendingCharacterData.core_layers.head.rendered_url,
                    web_url: finalUrls.webHeadUrl || finalUrls.headUrl || pendingCharacterData.core_layers.head.web_url,
                  } : pendingCharacterData.core_layers.head,
                },
                nsfw_body_url: finalUrls.nsfwUrl || pendingCharacterData.nsfw_body_url,
              };

              // 3. Create and save passport data
              const originalRefSheetUrl = finalUrls.referenceSheetUrl || referenceSheetUrl;
              const unclothedRefSheetUrl = finalUrls.unclothedReferenceSheetUrl || referenceSheetUnclothedUrl;
              const finalRefSheetUrl = originalRefSheetUrl;
              // Keep copied URLs in the resumable draft if either database save fails.
              const resumableCharacter: Character2DLayered = {
                ...finalizedCharacterData,
                builder_session: {
                  ...pendingCharacterData.builder_session,
                  phase: 'reference-sheet-preview',
                  feature_editor: pendingCharacterData.builder_session?.feature_editor ? {
                    ...pendingCharacterData.builder_session.feature_editor,
                    head: pendingCharacterData.builder_session.feature_editor.head ? {...pendingCharacterData.builder_session.feature_editor.head, imageUrl: finalUrls.headUrl || pendingCharacterData.builder_session.feature_editor.head.imageUrl} : null,
                    lockedHeadUrl: finalUrls.headUrl || pendingCharacterData.builder_session.feature_editor.lockedHeadUrl,
                    body: pendingCharacterData.builder_session.feature_editor.body ? {...pendingCharacterData.builder_session.feature_editor.body, imageUrl: finalUrls.transposedBodyUrl || finalUrls.bodyUrl || pendingCharacterData.builder_session.feature_editor.body.imageUrl} : null,
                  } : undefined,
                  reference_sheet_url: originalRefSheetUrl,
                  reference_sheet_unclothed_url: unclothedRefSheetUrl || undefined,
                  reference_sheet_version: 'original',
                  finalization_temp_urls: [...new Set([
                    ...(pendingCharacterData.builder_session?.finalization_temp_urls || []),
                    ...finalUrls.tempUrlsToDelete,
                  ])],
                  passport_data: pendingPassportData,
                },
              };
              setPendingCharacterData(resumableCharacter);
              setReferenceSheetUrl(originalRefSheetUrl);
              setReferenceSheetUnclothedUrl(unclothedRefSheetUrl);
              setDualViewportResult(null);
              await saveCharacter2D({ ...resumableCharacter, project_id: selectedProjectId });
              const passport: CharacterPassport = {
                id: pendingCharacterData.passport_id || `${pendingCharacterData.id}_passport`,
                character_id: finalizedCharacterData.id,
                user_id: user.id,
                reference_sheet: {
                  front_url: finalRefSheetUrl,
                  back_url: finalRefSheetUrl,
                  closeup_url: finalRefSheetUrl,
                  turnaround_url: finalRefSheetUrl,
                  original_url: originalRefSheetUrl,
                  unclothed_url: unclothedRefSheetUrl || undefined,
                },
                text_description: pendingPassportData.textData.full_description,
                asset_manifest: {
                  body: finalizedCharacterData.core_layers.body ? {
                    asset_id: finalizedCharacterData.core_layers.body.id,
                    description: finalizedCharacterData.core_layers.body.prompt_used || '',
                  } : undefined,
                  head: finalizedCharacterData.core_layers.head ? {
                    asset_id: finalizedCharacterData.core_layers.head.id,
                    description: finalizedCharacterData.core_layers.head.prompt_used || '',
                  } : undefined,
                },
                usage_prompt: pendingPassportData.usage_prompt,
                character_data: finalizedCharacterData,
                created_at: Date.now(),
                updated_at: Date.now(),
              };
              await saveCharacterPassport(passport);

              // 4. Update character description from passport and mark as finalized
              // Also ensures the final URLs are saved to the character record
              const characterToSave = {
                ...finalizedCharacterData,
                description: pendingPassportData.textData.full_description,
                is_finalized: true,
                passport_id: passport.id,
                passport_generated_at: passport.created_at,
                builder_session: undefined,
                updated_at: Date.now(),
              };
              await saveCharacter2D({ ...characterToSave, project_id: selectedProjectId });

              showToast('Character created successfully!', 'success');

              // Trigger refetch of character list in parent component
              if (setRefetchTrigger) {
                setRefetchTrigger(prev => prev + 1);
              }

              // 5. Navigate to character profile page
              setShowCharacterProfile(finalizedCharacterData.id);
              updateUrlState({
                projectId: selectedProjectId || undefined,
                view: 'profile',
                characterId: finalizedCharacterData.id,
              });

              // Clear builder phase state
              setBuilderPhase(null);
              setPromptBuilderData(null);
              setDualReviewData(null);
              setSelectedArchetype(null);
              setSelectedModel(null);
              setCharacterName('');
              setReferenceSheetUrl(null);
              setReferenceSheetUnclothedUrl(null);
              setReferenceSheetVersion('original');
              setPendingCharacterData(null);
              setPendingPassportData(null);
              clearBuilderPhaseData();
              void cleanupTempCharacterImages(resumableCharacter.builder_session?.finalization_temp_urls || []);
            } catch (error) {
              console.error('Failed to finalize character:', error);
              showToast('Failed to finalize character. Please try again.', 'error');
            } finally {
              setIsSavingReferenceSheet(false);
            }
          }}
          onBack={() => {
            const returnPhase = pendingCharacterData.builder_session?.feature_editor ? 'feature-editor' : 'dual-viewport';
            setExistingCharacter(pendingCharacterData);
            setBuilderPhase(returnPhase);
            setReferenceSheetUrl(null);
            setReferenceSheetUnclothedUrl(null);
            setReferenceSheetVersion('original');
            setPendingPassportData(null);
            // Keep pendingCharacterData since we might come back
            updateUrlState({
              projectId: selectedProjectId || undefined,
              phase: returnPhase,
              characterId: pendingCharacterData.id,
            });
          }}
        />
      </div>
    );
  }

  // Loading/transition state - show minimal UI while phase initializes
  return (
    <div className="flex-1 flex items-center justify-center min-h-screen bg-onyx">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-slate-blue border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    </div>
  );
};

